<template>
  <base-slide-over size="3xl" ref="slideOver" title="Cancel Plan" @hiding="reset()">
    <base-content v-show="plan.status != 'Awaiting Payment'">
      <div class="tw-flex tw-flex-col">
        <div class="tw-text-gray-800 tw-font-normal">Are you sure you want to cancel this plan?</div>
        <div class="tw-text-gray-500 tw-text-sm">
          <span>Cancelling this plan will stop all {{ organization.customerLabelSingular }} payments immediately.</span>
          <span class="tw-ml-1" v-if="organization.useCreditsSetting === true"
            >When a plan is cancelled the {{ organization.customerLabelSingular }}'s credits must be used within 60 days before they are permanently deleted.</span
          >
        </div>
        <base-card class="tw-mt-5 tw-grid sm:tw-grid-cols-2 tw-gap-4 tw-bg-gray-100">
          <div>
            <base-field class="tw-mb-3" title="Plan started On" :value="formattedStartDate" />
            <base-field title="Agreement Term" :value="formattedTermText" />
          </div>
          <div>
            <base-field class="tw-mb-3" title="Cycle">every {{ plan.cycleFrequency }} {{ plan.cycleInterval }}</base-field>
            <base-field title="Cost" :value="formattedPrice" />
          </div>
        </base-card>
        <base-form ref="reason" v-model="isReasonFormValid" class="tw-mt-5">
          <base-text-field label="Reason for cancelling" placeholder="Please enter a reason for cancelling the plan." required v-model="reason" />
          <div class="tw-flex" v-if="plan.disableCancellationEmails == false">
            <base-toggle label="Auto Renew Plan" v-model="suppressEmail" />
            <span class="tw-text-gray-500 tw-text-base tw-font-sm tw-ml-2">Do not send cancellation emails</span>
          </div>
        </base-form>
        <base-card class="tw-flex tw-flex-nowrap tw-mt-6" :class="{ 'tw-bg-primary-100': cancelOption === 'cancel' }">
          <base-radio class="tw-mr-2" value="cancel" name="cancelOption" v-model="cancelOption"></base-radio>
          <div class="tw-flex-grow">
            <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Cancel Plan - No Fee</div>
            <div class="tw-text-gray-500 tw-text-sm">
              <span>
                Cancel the plan immediately with no additional charges.
              </span>
              <span class="tw-ml-1" v-if="organization.useCreditsSetting === true">{{ organization.customerLabelSingular }} has 60 days to use credits.</span>
            </div>
          </div>
        </base-card>
        <base-card class="tw-flex tw-flex-nowrap tw-mt-3" :class="{ 'tw-bg-primary-100': cancelOption === 'cycle' }">
          <base-radio class="tw-mr-2" value="cycle" name="cancelOption" v-model="cancelOption"></base-radio>
          <div class="tw-flex-grow">
            <div class="tw-flex">
              <div class="tw-flex-auto">
                <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Complete plan billing cycles</div>
                <div class="tw-text-gray-500 tw-text-sm">
                  <span>Charge {{ organization.customerLabelSingular }} for remaining billing cycles @ {{ formattedPrice }}.</span>
                  <span class="tw-ml-1" v-if="organization.useCreditsSetting === true">Applicable credits will be provisioned and must be used in 60 days.</span>
                </div>
              </div>
              <base-text-field label="Number of cycles" v-model="cycles" type="number" :disabled="cancelOption !== 'cycle'" />
            </div>
          </div>
        </base-card>
        <base-card class="tw-flex tw-flex-nowrap tw-mt-3" :class="{ 'tw-bg-primary-100': cancelOption === 'fee' }">
          <base-radio class="tw-mr-2" value="fee" name="cancelOption" v-model="cancelOption"></base-radio>
          <div class="tw-flex-grow">
            <div class="tw-flex">
              <div class="tw-flex-auto">
                <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Early Termination Fee</div>
                <div class="tw-text-gray-500 tw-text-sm">
                  <span> Charge {{ organization.customerLabelSingular }} an early termination fee. </span>
                  <span class="tw-ml-1" v-if="organization.useCreditsSetting === true">{{ organization.customerLabelSingular }} has 60 days to use credits.</span>
                </div>
              </div>
              <base-currency-input label="Fee amount" placeholder="$0.00" :int-value.sync="feeCents" :disabled="cancelOption !== 'fee'" />
            </div>
          </div>
        </base-card>
        <div class="sm:tw-flex sm:tw-flex-row-reverse tw-mt-4">
          <base-button @click="cancelPlan()" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3" :disabled="!isReasonFormValid">{{ cancelButtonText }}</base-button>
          <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
            Close
          </base-button>
        </div>
      </div>
      <inline-error id="cancel-business-member-plan-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-content>
    <base-content v-show="plan.status === 'Awaiting Payment'">
      <div>
        <div class="tw-mt-3 tw-text-gray-800 tw-font-normal">Are you sure you want to cancel this plan?</div>
        <div class="sm:tw-flex sm:tw-flex-row-reverse tw-mt-4">
          <base-button @click="hardCancelPlan()" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3" :disabled="!isReasonFormValid">{{ cancelButtonText }}</base-button>
          <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
            Close
          </base-button>
        </div>
        <inline-error id="cancel-business-member-plan-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
      </div>
    </base-content>
  </base-slide-over>
</template>
<script>
import moment from "moment";
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";

export default {
  name: "BusinessMemberPlanCancel",
  components: { InlineError },
  props: {
    businessId: String,
    userId: String,
    plan: Object,
    isAutoActions: Boolean,
  },
  data() {
    return {
      isLoading: false,
      feeCents: 0,
      cycles: 0,
      cancelOption: "",
      reason: null,
      isReasonFormValid: true,
      suppressEmail: false,
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    locale() {
      return window.navigator.language;
    },
    formattedPrice() {
      return new Intl.NumberFormat(this.locale, { style: "currency", currency: this.plan.currency }).format(this.plan.priceCents / 100);
    },
    totalCycleCost() {
      return this.cycles
        ? new Intl.NumberFormat(this.locale, { style: "currency", currency: this.plan.currency }).format((this.cycles * this.plan.priceCents) / 100)
        : new Intl.NumberFormat(this.locale, { style: "currency", currency: this.plan.currency }).format(0);
    },
    formattedFee() {
      return this.feeCents
        ? new Intl.NumberFormat(this.locale, { style: "currency", currency: this.plan.currency }).format(this.feeCents / 100)
        : new Intl.NumberFormat(this.locale, { style: "currency", currency: this.plan.currency }).format(0);
    },
    cancelButtonText() {
      if (this.isAutoActions) {
        return "Cancel With Auto Deletion"; // Custom text for auto action
      }
      switch (this.cancelOption) {
        case "cancel":
          return "Cancel Plan";
        case "cycle":
          return `Charge ${this.totalCycleCost} and cancel plan`;
        case "fee":
          return `Charge ${this.formattedFee} and cancel plan`;
        default:
          return "Cancel Plan";
      }
    },
    formattedTermText() {
      if (this.plan.termInterval) {
        switch (this.plan.termInterval) {
          case "month":
            return "Month-to-Month";
          case "year":
            return "12 months";
          case "week":
            return "Weekly";
          case "day":
            return "Daily";
          default:
            throw new Error(`Unknown billing : ${this.plan.termInterval}`);
        }
      }
      return "";
    },
    formattedStartDate: {
      get() {
        return moment(this.plan.startDate)
          .utc()
          .local()
          .format("MM/DD/YYYY");
      },
    },
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.cancelOption = "cancel";
    },
    hide() {
      this.errors = null;
      this.$refs.slideOver.hide();
    },
    reset() {
      this.$refs.reason.reset();
      this.feeCents = 0;
      this.cycles = 0;
    },
    cancelPlan() {
      let cycles = 0;
      let feeCents = 0;

      if (!this.$refs.reason.validate()) return;

      if (this.cancelOption === "cycle") cycles = this.cycles;
      if (this.cancelOption === "fee") feeCents = this.feeCents;
      const suppressEmail = this.plan.disableCancellationEmails === true ? true : this.suppressEmail;

      this.isLoading = true;
      this.$axios
        .put(`/businesses/${this.businessId}/members/${this.userId}/memberplans/${this.plan.userBusinessPlanId}/cancel`, {
          cycles: cycles,
          feeCents: feeCents,
          reason: this.reason,
          suppressEmail: suppressEmail,
          isAutoActions: this.isAutoActions,
        })
        .then(() => {
          this.hide();
          this.cycles = 0;
          this.feeCents = 0;
          this.reason = null;
          this.suppressEmail = false;
          this.EventBus.$emit("member_update");
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Member Plan Cancelled", message: "Plan was cancelled successfully" });
        })
        .catch((err) => {
          this.errorTitle = "Error cancelling plan";
          this.errorMessage = "Sorry, we had trouble cancelling this plan. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    hardCancelPlan() {
      this.isLoading = true;
      this.reason = "Business Manager Cancelled - Plan had incomplete payment";

      this.$axios
        .put(`/businesses/${this.businessId}/members/${this.userId}/memberplans/${this.plan.userBusinessPlanId}/cancel`, {
          cycles: 0,
          feeCents: 0,
          reason: this.reason,
          suppressEmail: true,
        })
        .then(() => {
          this.isLoading = false;
          this.hide();
          this.EventBus.$emit("member_update");
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Member Plan Cancelled", message: "Plan was cancelled successfully" });
        })
        .catch((err) => {
          this.errorTitle = "Error cancelling plan";
          this.errorMessage = "Sorry, we had trouble cancelling this plan. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
